import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  CustomBlockingRulesOptions,
  CustomBlockingRulesSubdomain,
  type PublisherInventoryRule,
} from '@openx/types/targeting/inventoryAndContent';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INVENTORY_IDS_DISPLAY_LIMIT } from '../../shared/InventoryAndContent/constants';
import { Items } from '../../shared/SelectedItems/Items';
import { ShowHideButton } from '../../shared/SelectedItems/ShowHideButton';
import { StyledItemsRemoveIcon } from '../../shared/SelectedItems/styled/Items.styled';
import { customBlockingRulesSubtitle } from '../InventoryAndContentTopBox/constants';

export type CustomBlockingRulesItemProps = {
  onRemoveAll: () => void;
  onRemoveSelectedRule: (rule: PublisherInventoryRule) => void;
  onRemoveSingleEntry: (item: string | JSX.Element, rule: PublisherInventoryRule) => void;
  readonly: boolean;
  customRule: PublisherInventoryRule;
};

export const CustomBlockingRulesItem = ({
  onRemoveAll,
  onRemoveSelectedRule,
  onRemoveSingleEntry,
  readonly,
  customRule,
}: CustomBlockingRulesItemProps) => {
  const { t } = useTranslation();
  const [displayLimit, setDisplayLimit] = useState(true);

  const itemsDisplayLimit = INVENTORY_IDS_DISPLAY_LIMIT;
  const isDomainRule = !!customRule.domain?.length;
  const currentRuleDomainType = isDomainRule ? CustomBlockingRulesOptions.DOMAIN : CustomBlockingRulesSubdomain;

  const currentItemType =
    customRule.blockingRuleOption === CustomBlockingRulesOptions.BUNDLE_ID
      ? CustomBlockingRulesOptions.BUNDLE_ID
      : currentRuleDomainType;

  const currentItemTypeLength = customRule[currentItemType]?.length;

  if (currentItemTypeLength === 0) return null;

  const shouldDisplayShowMoreButton = currentItemTypeLength > itemsDisplayLimit;
  const overflowCount = currentItemTypeLength - itemsDisplayLimit;
  const notDisplayedText = t('and {overflowCount} more.', { overflowCount });
  const itemsSubtitle = customBlockingRulesSubtitle[currentItemType];

  const itemsToDisplay = displayLimit
    ? customRule[currentItemType].slice(0, itemsDisplayLimit)
    : customRule[currentItemType];

  return (
    <Stack
      key={customRule.publisher_id}
      direction="row"
      sx={{
        gap: 1,
        mb: 2,
      }}
    >
      <StyledItemsRemoveIcon disabledStyle={false} sx={{ mr: 0.5, mt: 0.5 }} />
      <Stack data-test="blocked-publisher-id-container" direction="column" sx={{ mr: 1 }}>
        <Box sx={{ '&:hover': { '.MuiIconButton-root': { visibility: 'visible' } } }}>
          <Typography
            data-test="blocked-publisher-id"
            sx={{
              color: 'text.secondary',
              mb: 1,
            }}
          >
            {t('Publisher ID: ')}

            <Typography
              component="span"
              sx={{
                color: 'text.primary',
              }}
            >
              {customRule.publisher_id}
            </Typography>

            {!readonly && (
              <IconButton
                sx={{ ml: 1, mb: '2px', visibility: 'hidden' }}
                onClick={() => onRemoveSelectedRule(customRule)}
                size="large"
                data-test="remove-custom-rule-button"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Typography>
        </Box>

        <Items
          onRemove={item => onRemoveSingleEntry(item, customRule)}
          onRemoveAll={onRemoveAll}
          selectedItems={itemsToDisplay}
          title={''}
          readonly={readonly}
          isAddIcon={false}
          disabledStyle={false}
          hideIcon
          hideTitle
          customSubtitle={t(itemsSubtitle)}
          data-test="blocking-rule"
        />

        <Box>
          {shouldDisplayShowMoreButton && (
            <ShowHideButton
              isDisplayLimit={displayLimit}
              onClick={() => setDisplayLimit(!displayLimit)}
              notDisplayedText={notDisplayedText}
            >
              {displayLimit ? t('show more') : t('hide')}
            </ShowHideButton>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
